// src/pages/ProfilePage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  User,
  Mail,
  Shield,
  ArrowLeft,
  Edit2,
  Check,
  X,
  Phone,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  KeyRound,
} from "lucide-react";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const ProfilePage = () => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ full_name: "" });
  const [profileLoading, setProfileLoading] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ password: "", confirm_password: "" });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordJustChanged, setPasswordJustChanged] = useState(false);

  // Quick, honest strength read — no fake "strong" for a 6-char word.
  const getPasswordStrength = (val) => {
    if (!val) return { score: 0, label: "", color: "" };
    let score = 0;
    if (val.length >= 6) score++;
    if (val.length >= 10) score++;
    if (/[A-Z]/.test(val) && /[a-z]/.test(val)) score++;
    if (/[0-9]/.test(val)) score++;
    if (/[^A-Za-z0-9]/.test(val)) score++;
    const levels = [
      { label: "Too short", color: "bg-red-500" },
      { label: "Weak", color: "bg-red-500" },
      { label: "Okay", color: "bg-amber-500" },
      { label: "Good", color: "bg-amber-500" },
      { label: "Strong", color: "bg-green-500" },
      { label: "Very strong", color: "bg-green-500" },
    ];
    return { score, ...levels[Math.min(score, levels.length - 1)] };
  };
  const passwordStrength = getPasswordStrength(passwordForm.password);

  const hasFetchedRef = useRef(false);

  // Fetch Profile
  const fetchUserProfile = async () => {
    const storedUser = JSON.parse(localStorage.getItem("user")) || {};
    const userId = storedUser?.user_id || storedUser?.id || 1;

    try {
      setLoading(true);
      const response = await apiCall(
        "get",
        `/get-user-by-id/${userId}`,
        null,
        navigate,
        false,
        false
      );

      if (response?.success === true && response?.data) {
        const user = response.data;
        setUserData(user);
        localStorage.setItem("user", JSON.stringify(user));

        setProfileForm({ full_name: user.full_name || "" });
      } else {
        toast.error(response?.message || "Failed to load profile");
      }
    } catch (err) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchUserProfile();
  }, []);

  // Save Profile (Only full_name for now)
  const handleProfileSave = async () => {
    if (!profileForm.full_name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }

    setProfileLoading(true);
    try {
      const userId = userData?.user_id || userData?.id;

      const response = await apiCall(
        "put",
        `/update-user/${userId}`, // Change this if your update endpoint is different
        { full_name: profileForm.full_name },
        navigate,
        false,
        true
      );

      if (response?.success === true) {
        const updatedUser = { ...userData, full_name: profileForm.full_name };
        setUserData(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));

        toast.success("Profile updated successfully");
        setIsEditingProfile(false);
      }
    } catch (err) {
      toast.error("Failed to update profile");
    } finally {
      setProfileLoading(false);
    }
  };

  // Change Password
  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((p) => ({ ...p, [name]: value }));
    if (passwordErrors[name]) setPasswordErrors((p) => ({ ...p, [name]: "" }));
  };

  const validatePasswordForm = () => {
    const errs = {};
    if (!passwordForm.password.trim()) {
      errs.password = "Password is required";
    } else if (passwordForm.password.trim().length < 6) {
      errs.password = "Password must be at least 6 characters";
    }
    if (!passwordForm.confirm_password.trim()) {
      errs.confirm_password = "Please confirm your password";
    } else if (passwordForm.confirm_password !== passwordForm.password) {
      errs.confirm_password = "Passwords do not match";
    }
    setPasswordErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) return;

    const userId = userData?.user_id || userData?.id;

    setPasswordLoading(true);
    try {
      const response = await apiCall(
        "PUT",
        "/users/change-password",
        {
          user_id: userId?.toString(),
          new_password: passwordForm.password,
        },
        navigate
      );

      if (response?.success === true) {
        setPasswordForm({ password: "", confirm_password: "" });
        setPasswordErrors({});
        setShowPassword(false);
        setShowConfirmPassword(false);
        setPasswordJustChanged(true);
        setTimeout(() => setPasswordJustChanged(false), 2200);
      }
    } catch (err) {
      toast.error("Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <LoadingComponent />
      </div>
    );
  }

  const u = userData || {};

  return (

    <div className="w-full space-y-5 py-2 px-10  overflow-y-auto animate-in fade-in slide-in-from-bottom-2 duration-500">

      <Tabs defaultValue="profile" className="w-full">
        <TabsList>
          <TabsTrigger value="profile" className="gap-1.5">
            <User className="w-3.5 h-3.5" />
            Profile Information
          </TabsTrigger>
          <TabsTrigger value="password" className="gap-1.5">
            <Lock className="w-3.5 h-3.5" />
            Change Password
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden transition-shadow hover:shadow-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">Profile Information</span>
              </div>
              <div className="flex justify-start pb-2">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to Profile
        </button>
      </div>

              {/* {!isEditingProfile ? (
                <button
                  onClick={() => setIsEditingProfile(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  Edit
                </button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => {
                      setIsEditingProfile(false);
                      setProfileForm({ full_name: u.full_name || "" });
                    }}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                    Cancel
                  </button>
                  <button
                    onClick={handleProfileSave}
                    disabled={profileLoading}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg disabled:opacity-60"
                  >
                    {profileLoading ? (
                      <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                    Save
                  </button>
                </div>
              )} */}
            </div>

            <div className="p-5 space-y-5">
              {/* Avatar + Name */}
              <div className="flex items-center gap-4">
                <div className="relative flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary/60 p-[3px] shadow-sm">
                    <div className="w-full h-full rounded-full bg-card flex items-center justify-center text-primary font-bold text-3xl">
                      {(u.full_name || "U").charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <span
                    className={`absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full border-2 border-card ${
                      u.status === "active" ? "bg-green-500" : "bg-muted-foreground"
                    }`}
                    title={u.status === "active" ? "Active" : "Inactive"}
                  >
                    {u.status === "active" && (
                      <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-75" />
                    )}
                  </span>
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {isEditingProfile ? profileForm.full_name : u.full_name || "—"}
                  </p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                    {u.username || "—"}
                    {u.role_name && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                        <ShieldCheck className="w-3 h-3" />
                        {u.role_name}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Full Name */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    Full Name
                  </label>
                  {isEditingProfile ? (
                    <input
                      type="text"
                      value={profileForm.full_name}
                      onChange={(e) => setProfileForm((p) => ({ ...p, full_name: e.target.value }))}
                      className="w-full px-4 py-3 text-sm bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      placeholder="Enter full name"
                    />
                  ) : (
                    <div className="px-4 py-3 text-sm bg-muted/30 border border-border rounded-xl text-foreground">
                      {u.full_name || "—"}
                    </div>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Mail className="w-4 h-4" />
                    Email Address
                  </label>
                  <div className="px-4 py-3 text-sm bg-muted/30 border border-border rounded-xl flex items-center justify-between">
                    <span>{u.email || "—"}</span>
                    {/* <span className="text-[10px] px-2.5 py-1 bg-muted rounded-full text-muted-foreground">Read-only</span> */}
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Phone className="w-4 h-4" />
                    Phone Number
                  </label>
                  <div className="px-4 py-3 text-sm bg-muted/30 border border-border rounded-xl text-foreground">
                    {u.phone || "—"}
                  </div>
                </div>

                {/* Username */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    Username
                  </label>
                  <div className="px-4 py-3 text-sm bg-muted/30 border border-border rounded-xl text-foreground">
                    {u.username || "—"}
                  </div>
                </div>
              </div>

              {/* Role & Status in ONE LINE */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Role */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Shield className="w-4 h-4" />
                    Role
                  </label>
                  <div className="px-4 py-3 text-sm bg-muted/30 border border-border rounded-xl flex items-center gap-2 text-foreground">
                    <span className="w-2.5 h-2.5 rounded-full bg-primary inline-block" />
                    {u.role_name || "—"}
                  </div>
                </div>

                {/* Status */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Status</label>
                  <div className="px-4 py-3 text-sm bg-muted/30 border border-border rounded-xl flex items-center gap-2">
                    <span
                      className={`w-2.5 h-2.5 rounded-full inline-block ${
                        u.status === "active" ? "bg-green-500" : "bg-red-500"
                      }`}
                    />
                    <span className="capitalize">{u.status || "—"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Password Tab */}
        <TabsContent value="password">
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden transition-shadow hover:shadow-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">Change Password</span>
              </div>
              <div className="flex justify-start pb-2">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to Profile
        </button>
      </div>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* New Password */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Lock className="w-4 h-4" />
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={passwordForm.password}
                      onChange={handlePasswordInputChange}
                      placeholder="Enter new password"
                      className={`w-full px-4 py-3 pr-11 text-sm bg-background border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow ${
                        passwordErrors.password ? "border-red-500" : "border-border"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordForm.password && (
                    <div className="flex items-center gap-2 pt-0.5">
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden flex gap-0.5">
                        {[0, 1, 2, 3, 4].map((i) => (
                          <span
                            key={i}
                            className={`flex-1 h-full rounded-full transition-colors duration-300 ${
                              i < passwordStrength.score ? passwordStrength.color : "bg-muted"
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-[10px] font-medium text-muted-foreground w-16 text-right">
                        {passwordStrength.label}
                      </span>
                    </div>
                  )}
                  {passwordErrors.password && (
                    <p className="text-xs text-red-500">{passwordErrors.password}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Lock className="w-4 h-4" />
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirm_password"
                      value={passwordForm.confirm_password}
                      onChange={handlePasswordInputChange}
                      placeholder="Confirm new password"
                      className={`w-full px-4 py-3 pr-11 text-sm bg-background border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
                        passwordErrors.confirm_password ? "border-red-500" : "border-border"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((p) => !p)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordErrors.confirm_password && (
                    <p className="text-xs text-red-500">{passwordErrors.confirm_password}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleChangePassword}
                  disabled={passwordLoading}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 disabled:opacity-60 ${
                    passwordJustChanged
                      ? "bg-green-600 text-white"
                      : "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95"
                  }`}
                >
                  {passwordLoading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : passwordJustChanged ? (
                    <Check className="w-4 h-4 animate-in zoom-in duration-300" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  {passwordJustChanged ? "Changed!" : "Change Password"}
                </button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>      
    </div>
  );
};

export default ProfilePage;